import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/SessionProvider";
import { ApiError } from "@/lib/api";
import type { MeEntitlementsResponse, ModuleEntitlement } from "@/lib/types.entitlements";
import { useApiClient } from "./useApi";

function isModuleEntitlement(value: unknown): value is ModuleEntitlement {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.product_code === "string" &&
    typeof candidate.service_code === "string" &&
    typeof candidate.module_code === "string" &&
    Array.isArray(candidate.campaign_codes) &&
    candidate.campaign_codes.every((campaignCode) => typeof campaignCode === "string")
  );
}

function isEntitlementsResponse(value: unknown): value is MeEntitlementsResponse {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.org_id === "string" && Array.isArray(candidate.modules) && candidate.modules.every(isModuleEntitlement);
}

/**
 * Reads only RADAR's selected-organisation entitlement contract. Query keys include the
 * organisation id, so an entitlement from a previous selection cannot render for the next one.
 */
export function useEntitlements(): UseQueryResult<MeEntitlementsResponse> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["me", "entitlements", orgId],
    enabled: orgId !== null,
    queryFn: async () => {
      const response = await api.get<unknown>("/me/entitlements");
      if (!isEntitlementsResponse(response)) {
        throw new ApiError({
          token: "internal_error",
          message: "The entitlement response did not match the RADAR contract.",
          httpStatus: 502,
          correlationId: null,
        });
      }
      if (response.org_id !== orgId) {
        throw new ApiError({
          token: "forbidden",
          message: "The entitlement response belongs to another organisation.",
          httpStatus: 403,
          correlationId: null,
        });
      }
      return response;
    },
    // Entitlements are an authority hint, not ordinary page data. Do not retain a stale
    // answer across mounts or selected-organisation changes; the server remains the final
    // authority on every adapter request.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}

export function hasModuleEntitlement(
  entitlements: MeEntitlementsResponse | undefined,
  moduleCode: string,
): boolean {
  return entitlements?.modules.some((module) => module.module_code === moduleCode) === true;
}
